import { readFileAsDataURL, uuid } from "./util";
import axios from "axios";
import moment from "moment";

export const REACTIONS = {
  ThumbsUp: { token: "thumbs-up", sortOrder: 1, iconName: "thumb_up" },
  ThumbsDown: { token: "thumbs-down", sortOrder: 2, iconName: "thumb_down" },
  Star: { token: "star", sortOrder: 3, iconName: "star" },
  Heart: { token: "heart", sortOrder: 4, iconName: "favorite" },
  Laugh: { token: "laugh", sortOrder: 5, iconName: "emoji_emotions" },
  Exclamation: {
    token: "exclamation",
    sortOrder: 6,
    iconName: "priority_high"
  },
  Question: { token: "question", sortOrder: 7, iconName: "question_mark" },
  _UNKNOWN: { token: null, sortOrder: -1, iconName: "stop" },
  toList() {
    return [
      REACTIONS.ThumbsUp,
      REACTIONS.ThumbsDown,
      REACTIONS.Star,
      REACTIONS.Heart,
      REACTIONS.Laugh,
      REACTIONS.Exclamation,
      REACTIONS.Question
    ];
  },
  toMap() {
    return {
      "thumbs-up": REACTIONS.ThumbsUp,
      "thumbs-down": REACTIONS.ThumbsDown,
      star: REACTIONS.Star,
      heart: REACTIONS.Heart,
      laugh: REACTIONS.Laugh,
      exclamation: REACTIONS.Exclamation,
      question: REACTIONS.Question
    };
  },
  reaction(token) {
    const r = REACTIONS.toMap()[token];
    return r ? r : REACTIONS._UNKNOWN;
  },
  iconName(token) {
    return REACTIONS.reaction(token).iconName;
  },
  sortOrder(token) {
    return REACTIONS.reaction(token).sortOrder;
  }
};

const MOMENT_CALENDAR_FORMAT = {
  lastDay: "[Yesterday] LT",
  sameDay: "LT",
  nextDay: "[Tomorrow] LT",
  lastWeek: "ddd LT",
  nextWeek: "[Next] ddd LT",
  sameElse: "M/D/YY"
  /*
	  lastDay: '[Yesterday] LT',
	  sameDay: 'LT',
	  nextDay: '[Tomorrow]',
	  lastWeek: 'M/D/YY',
	  nextWeek: '[Next] dddd',
	  sameElse: 'M/D/YY'
	   */
};

/**
 * Creates a placeholder message to mimic a twilio message (temporarily) while it is being sent
 * @returns {Promise<string>}
 */
export async function createSendingMessage(file, user) {
  throw new Error("Deprecated");
  //const dataUrl = await readFileAsDataURL(file);
  //console.log(user);
  //return {
  //	isSending: true,
  //	sid: await uuid(),
  //	body: dataUrl,
  //	author: user.id,
  //	attributes: { contentType: file.type },
  //	progress: 0
  //};
}

/**
 * Creates a placeholder text message to mimic a twilio message (temporarily) while it is being sent
 */
export async function createTempMessage(
  channel,
  body,
  language,
  user,
  contentType,
  width,
  height,
  duration
) {
  const tempId = await uuid();
  contentType = contentType ? contentType : "text/plain";
  const tempMessage = {
    channel: channel,
    isTemp: true,
    media: contentType && !contentType.startsWith("text/"),
    sid: tempId,
    body: body,
    author: user.id,
    attributes: { tempId: tempId },
    progress: 0, // not sure we can use this
    dateCreated: new Date(),
    errorMessage: null
  };

  if (contentType) tempMessage.attributes.contentType = contentType;
  if (language) tempMessage.attributes.language = language;
  if (width) tempMessage.attributes.width = width;
  if (height) tempMessage.attributes.height = height;
  if (duration) tempMessage.attributes.duration = duration;
  if (user)
    tempMessage.attributes.author = user.firstName + " " + user.lastName;

  return tempMessage;
}

/**
 * The push diagnostic conversation, which no one should ever see.
 *
 * It is a real conversation the user participates in, so Twilio hands it to every client that
 * enumerates conversations and each one has to filter it out for itself.
 *
 * Two ways of recognising it, because either can be absent. The attribute travels on the
 * conversation itself and works before the profile has loaded; the sid from the profile works even
 * for a conversation created before the attribute existed.
 *
 * @param diagnosticSid pushDiagnosticConversationSid from the user profile. May be null.
 */
export function isPushDiagnostic(conversation, diagnosticSid) {
  if (!conversation) return false;

  if (diagnosticSid && conversation.sid === diagnosticSid) return true;

  return conversation.attributes
    ? conversation.attributes["diagnostic"] === true
    : false;
}

export function hasUnread(conversation) {
  // console.log(
  //   "hasUnread[" + (channel ? channel.friendlyName : "NULL") + "]..."
  // );
  let unread = false;
  if (conversation.lastReadMessageIndex === null) {
    // console.log(
    //   "...hasUnread - channel.lastMessage.dateCreated " +
    //     (channel.lastMessage && channel.lastMessage.dateCreated)
    // );
    unread =
      conversation.lastMessage && conversation.lastMessage.dateCreated !== null;
  } else {
    // console.log(
    //   "...hasUnread - [" +
    //     channel.lastConsumedMessageIndex +
    //     " < " +
    //     channel.lastMessage.index +
    //     "]"
    // );
    unread =
      conversation.lastMessage &&
      conversation.lastReadMessageIndex < conversation.lastMessage.index;
  }
  return unread;
}

export function isText(message) {
  const contentType =
    message && message.attributes ? message.attributes["contentType"] : null;
  return contentType && contentType.indexOf("text/") === 0;
}

export function isImage(message) {
  const contentType =
    message && message.attributes ? message.attributes["contentType"] : null;
  return contentType && contentType.indexOf("image/") === 0;
}

export function isVideo(message) {
  const contentType =
    message && message.attributes ? message.attributes["contentType"] : null;
  return contentType && contentType.indexOf("video/") === 0;
}

export function isFile(message) {
  return !isText(message) && !isImage(message) && !isVideo(message);
}

export function videoType(message) {
  return message.attributes["contentType"];
}

export function mediaUrl(message, useOriginal, participantSid) {
  if (message.isTemp || !message.conversation) {
    return message.body;
  } else if (message.dataUrl) {
    return message.dataUrl;
  } else {
    const tempImg = "foo.jpg"; // todo: fix this, replace with valid static placeholder image url
    if (isImage(message)) {
      const now = new Date().getTime();
      const sent = message.dateUpdated.getTime();
      const oneMinute = 1000 * 60 * 60;
      if (now - sent < oneMinute) {
        // use original size image because the thumbnail might not be processed yet
        return (
          "/rest-api/chat/image/original/" +
          message.conversation.attributes.organizationId +
          "/" +
          message.conversation.sid +
          "/" +
          message.body
        );
      } else {
        // use thumbnail/small image
        return (
          "/rest-api/chat/image/" +
          (useOriginal ? "original" : "small") +
          "/" +
          message.conversation.attributes.organizationId +
          "/" +
          message.conversation.sid +
          "/" +
          message.body
        );
      }
    } else if (isVideo(message)) {
      return (
        "/rest-api/chat/video/" +
        message.conversation.attributes.organizationId +
        "/" +
        message.conversation.sid +
        "/" +
        message.body
      );
    } else {
      return tempImg;
    }
  }
}

/**
 *
 * @param dt
 * @returns {string|*}
 */
export function momentFriendly(dt) {
  if (!dt) {
    return "";
  } else if (!dt.isMoment) {
    dt = moment(dt);
  }

  return dt.calendar(null, MOMENT_CALENDAR_FORMAT);
}

export function conversationSortDate(conversation) {
  if (!conversation) return null;
  const dt = conversation.lastMessage
    ? conversation.lastMessage.dateCreated
    : conversation.dateUpdated
    ? conversation.dateUpdated
    : conversation.dateCreated;
  return dt;
}

export function conversationSortTimeMillis(conversation) {
  const dt = conversationSortDate(conversation);
  return dt ? dt.getTime() : 0;
}

/**
 * Most recent activity first.
 *
 * Copies before sorting: Array.sort mutates and returns the same reference, so sorting the reactive
 * array in place left Vue with an unchanged reference and nothing re-rendered. A new array is also
 * what makes a computed depending on this recompute.
 */
export function sortConversations(conversations) {
  return [...conversations].sort((a, b) => {
    const ta = conversationSortTimeMillis(a);
    const tb = conversationSortTimeMillis(b);
    return tb - ta;
  });
}

/** Index of the newest message, as this client currently sees it. */
export function lastMessageIndex(conversation) {
  return conversation && conversation.lastMessage
    ? conversation.lastMessage.index
    : null;
}

/** Preview text stamped on the conversation by the server, or "" when there is none yet. */
export function storedExcerptText(conversation) {
  const atts = conversation ? conversation.attributes : null;
  return atts && atts.excerpt ? atts.excerpt : "";
}

/** Message index the stored excerpt was taken from, or null on excerpts stamped before we tracked it. */
export function storedExcerptIndex(conversation) {
  const atts = conversation ? conversation.attributes : null;
  const index = atts ? atts.excerptIndex : null;
  return index === undefined ? null : index;
}

/**
 * Conversations whose stored excerpt no longer matches their newest message.
 *
 * Both values come free with a loaded conversation -- lastMessage.index from the SDK, excerptIndex
 * from the attributes the webhook stamps -- so finding the mismatches costs nothing. When the webhook
 * is healthy this is empty and no request gets made at all.
 */
export function staleExcerptConversations(conversations) {
  return (conversations || []).filter(conversation => {
    const index = lastMessageIndex(conversation);
    if (index === undefined || index === null) return false; // no messages, nothing to preview
    return storedExcerptIndex(conversation) !== index;
  });
}

/**
 * Repairs excerpts the webhook did not stamp -- it missed the event, Twilio never delivered it, or
 * the conversation has had no message since excerptIndex started being written.
 *
 * Not the normal path for keeping previews current: the webhook stamps the conversation attributes
 * and Twilio pushes that change to connected clients on its own. This is the reconciler for what
 * slipped through, so it is called with the mismatches and usually with nothing.
 *
 * Returns a sid -> {text, index, author} map, empty if there was nothing to do or the call failed --
 * a preview that cannot be repaired should leave the list working.
 */
export async function refreshExcerpts(conversations) {
  const stale = staleExcerptConversations(conversations);
  if (stale.length === 0) return {};

  try {
    const response = await axios.post("/rest-api/chat/refresh-excerpts", {
      conversations: stale.map(conversation => ({
        sid: conversation.sid,
        lastMessageIndex: lastMessageIndex(conversation)
      }))
    });

    const data = response.data || {};

    // Reported rather than thrown: a sid the caller no longer belongs to is normal churn, not a bug.
    if (data.errors && Object.keys(data.errors).length > 0) {
      console.warn("refresh-excerpts could not resolve:", data.errors);
    }

    return data.excerpts || {};
  } catch (err) {
    console.warn("refresh-excerpts failed: " + err);
    return {};
  }
}
